import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Button, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import issuesService from '../services/issuesService';

const IssueReporter = () => {
  const { currentUser } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState([]);
  const [initialising, setInitialising] = useState(true);

  const categories = useMemo(() => issuesService.getCategories(), []);
  const priorities = useMemo(() => issuesService.getPriorities(), []);

  useEffect(() => {
    if (!currentUser) return () => {};

    const unsubscribe = issuesService.subscribeToUserIssues(currentUser.uid, (userIssues) => {
      setIssues(userIssues || []);
      setInitialising(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!title.trim() || !description.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!currentUser) {
      setError('Please sign in to submit issues.');
      return;
    }

    try {
      setLoading(true);
      await issuesService.createIssue({
        title,
        description,
        category,
        priority,
        status: 'open',
        reportedBy: currentUser.uid,
        reportedByName: currentUser.displayName || currentUser.email,
        reportedByEmail: currentUser.email || '',
        createdAt: new Date().toISOString()
      });

      setSuccess('Issue reported successfully! Our team will review it shortly.');
      setTitle('');
      setDescription('');
      setCategory('general');
      setPriority('medium');
      setShowForm(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      devError('IssueReporter: failed to create issue', err);
      setError('Failed to report issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'open':
      default:
        return 'primary';
    }
  };

  const getPriorityColor = (value) => {
    switch (value) {
      case 'critical':
        return 'dark';
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'success';
    }
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Support &amp; Issues</h3>
        <Button variant="primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Report Issue'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {showForm && (
        <Card className="mb-4 shadow-sm border-0">
          <Card.Header className="bg-white">
            <h5 className="mb-0">Report New Issue</h5>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Brief description of the issue"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Provide detailed information about the issue"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  required
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Select value={category} onChange={(event) => setCategory(event.target.value)}>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select value={priority} onChange={(event) => setPriority(event.target.value)}>
                      {priorities.map((pri) => (
                        <option key={pri.id} value={pri.id}>
                          {pri.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Issue'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowForm(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white">
          <h5 className="mb-0">My Issues</h5>
        </Card.Header>
        <Card.Body>
          {initialising ? (
            <div className="text-center text-muted py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading your issues...
            </div>
          ) : issues.length > 0 ? (
            <div className="space-y-3">
              {issues.map((issue) => (
                <div key={issue.id} className="border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-1">{issue.title}</h6>
                      <small className="text-muted">#{issue.id}</small>
                    </div>
                    <div className="d-flex gap-2">
                      <Badge bg={getPriorityColor(issue.priority || 'medium')}>
                        {issue.priority || 'medium'}
                      </Badge>
                      <Badge bg={getStatusColor(issue.status || 'open')}>
                        {issue.status || 'open'}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-muted mb-2">{issue.description}</p>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Reported {issue.createdAt?.toDate?.()?.toLocaleString?.() || issue.createdAt || ''}
                    </small>
                    {issue.comments && issue.comments.length > 0 && (
                      <small className="text-muted">
                        {issue.comments.length} comment{issue.comments.length === 1 ? '' : 's'}
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              <p className="mb-0">You haven&apos;t reported any issues yet.</p>
              <Button variant="outline-primary" size="sm" className="mt-2" onClick={() => setShowForm(true)}>
                Report an Issue
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default IssueReporter;
